import React, { useEffect, useRef, useState } from 'react';
import { Body } from '@grnhse/seedling/lib/birch/typography';
import { withLocalePrefix } from 'shared/utils/translation';
import escape from 'lodash/escape';
import {
  getPendingExternalLinkFromMessage,
  type PendingExternalLink,
} from 'shared/utils/external_link_message';
import ExternalLinkConfirmationModal from './external_link_confirmation_modal';

const t = withLocalePrefix('talent_matching.document_viewer_modal');

type Props = {
  displayName: string;
  sourceUrl: string | undefined;
  fileType: string | null;
};

const ImageDisplay = ({
  displayName,
  sourceUrl,
}: {
  displayName: string;
  sourceUrl: string;
}) => {
  const sourceDocument = `
      <html>
        <head><style>body { margin: 0; } img { width: 100%; height: auto; }</style></head>
        <body><img src="${sourceUrl}" /></body>
      </html>
    `;

  return (
    <iframe
      aria-label={displayName}
      data-provides="document-entrypoint"
      srcDoc={sourceDocument}
      className="pii-blur"
    />
  );
};

const TextDisplay = ({
  displayName,
  sourceUrl,
}: {
  displayName: string;
  sourceUrl: string;
}) => {
  const [textContent, setTextContent] = React.useState<string | null>(null);

  React.useEffect(() => {
    const fetchTextContent = async () => {
      try {
        const response = await fetch(sourceUrl);
        const text = await response.text();
        setTextContent(text);
      } catch (error) {
        setTextContent(t('preview_not_available'));
      }
    };

    fetchTextContent();
  }, [sourceUrl]);

  const srcDoc = `<style>pre { margin: 0; white-space: pre-wrap; font-family: monospace; }</style>
    <pre>${escape(textContent || '')}</pre>`;

  return (
    <iframe
      aria-label={displayName}
      data-provides="document-entrypoint"
      srcDoc={srcDoc}
      className="pii-blur"
    />
  );
};

const DocPreviewDisplay = ({
  displayName,
  sourceUrl,
}: {
  displayName: string;
  sourceUrl: string;
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [pendingExternalLink, setPendingExternalLink] =
    useState<PendingExternalLink | null>(null);

  useEffect(() => {
    let documentOrigin: string;

    try {
      documentOrigin = new URL(sourceUrl, window.location.origin).origin;
    } catch (e) {
      return;
    }

    const handleIframeMessage = (event: MessageEvent) => {
      if (event.origin !== documentOrigin) {
        return;
      }
      if (event.source !== iframeRef.current?.contentWindow) {
        return;
      }

      const externalLink = getPendingExternalLinkFromMessage(event.data);
      if (!externalLink) {
        return;
      }

      // Acknowledge so pdf-viewer knows we're handling the link
      // and doesn't fall back to opening it directly
      iframeRef.current?.contentWindow?.postMessage(
        { type: 'ackExternalLinkClick' },
        documentOrigin
      );
      setPendingExternalLink(externalLink);
    };

    window.addEventListener('message', handleIframeMessage);
    return () => window.removeEventListener('message', handleIframeMessage);
  }, [sourceUrl]);

  const dismissModal = () => setPendingExternalLink(null);

  const openConfirmedExternalLink = () => {
    if (!pendingExternalLink) {
      return;
    }

    window.open(pendingExternalLink.url, '_blank', 'noopener,noreferrer');
    dismissModal();
  };

  return (
    <>
      <iframe
        ref={iframeRef}
        aria-label={displayName}
        data-provides="document-entrypoint"
        src={sourceUrl}
        className="pii-blur"
      />
      {pendingExternalLink && (
        <ExternalLinkConfirmationModal
          isOpen
          onDismiss={dismissModal}
          onConfirm={openConfirmedExternalLink}
          hostname={pendingExternalLink.hostname}
        />
      )}
    </>
  );
};

const DocumentDisplay = ({ displayName, sourceUrl, fileType }: Props) => {
  if (!sourceUrl) {
    return <Body>{t('preview_not_available')}</Body>;
  } else if (fileType === 'image') {
    return <ImageDisplay displayName={displayName} sourceUrl={sourceUrl} />;
  } else if (fileType === 'text') {
    return <TextDisplay displayName={displayName} sourceUrl={sourceUrl} />;
  } else {
    return (
      <DocPreviewDisplay displayName={displayName} sourceUrl={sourceUrl} />
    );
  }
};

export default DocumentDisplay;
